import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Zap, ArrowRight, Building2, Globe, Briefcase, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function OnboardingPage() {
  const { user, updateUser, updateToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [countries, setCountries] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    country_code: 'UK',
    trade_code: 'electrical'
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (user?.has_org) {
      navigate('/app');
      return;
    }
    
    // Fetch configs
    const fetchConfigs = async () => {
      try {
        const [tradesRes, countriesRes] = await Promise.all([
          api.get('/organisations/configs/trades'),
          api.get('/organisations/configs/countries')
        ]);
        setTrades(tradesRes.data);
        setCountries(countriesRes.data);
      } catch (error) {
        console.error('Failed to fetch configs', error);
      }
    };
    
    fetchConfigs();
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await api.post('/organisations', formData);
      const { organisation, access_token } = res.data;
      
      // Update auth state
      updateToken(access_token);
      updateUser({
        ...user,
        has_org: true,
        org_id: organisation.id
      });
      
      toast.success('Organisation created successfully!');
      navigate('/app');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create organisation';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-blueprint flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Zap className="w-10 h-10 text-orange-500" />
            <span className="font-black text-3xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              BIDSENTRY
            </span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Set Up Your Organisation</h1>
          <p className="text-slate-600">
            Tell us about your business to get started
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-8 h-2 rounded-sm transition-colors ${
                s <= step ? 'bg-orange-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-sm border border-slate-200 shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">Company Details</h2>
                    <p className="text-sm text-slate-500">Your organisation name</p>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="name">Company Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Smith Electrical Ltd"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="onboarding-name-input"
                  />
                </div>
                
                <Button 
                  type="button" 
                  className="w-full btn-primary"
                  onClick={() => formData.name && setStep(2)}
                  disabled={!formData.name}
                  data-testid="onboarding-next-1"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6 animate-fade-in">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                    <Globe className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">Location</h2>
                    <p className="text-sm text-slate-500">Where you operate</p>
                  </div>
                </div>
                
                <div>
                  <Label>Country / Region</Label>
                  <Select
                    value={formData.country_code}
                    onValueChange={(v) => setFormData({ ...formData, country_code: v })}
                  >
                    <SelectTrigger className="mt-2" data-testid="onboarding-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.country_code} value={c.country_code}>
                          {c.display_name} ({c.currency})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </Button>
                  <Button 
                    type="button" 
                    className="flex-1 btn-primary"
                    onClick={() => setStep(3)}
                    data-testid="onboarding-next-2"
                  >
                    Continue
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6 animate-fade-in">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">Your Trade</h2>
                    <p className="text-sm text-slate-500">Select your primary trade</p>
                  </div>
                </div>
                
                <div>
                  <Label>Trade / Industry</Label>
                  <Select
                    value={formData.trade_code}
                    onValueChange={(v) => setFormData({ ...formData, trade_code: v })}
                  >
                    <SelectTrigger className="mt-2" data-testid="onboarding-trade-select">
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                    <SelectContent>
                      {trades.map((t) => (
                        <SelectItem key={t.trade_code} value={t.trade_code}>
                          {t.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(2)}
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 btn-primary"
                    disabled={loading}
                    data-testid="onboarding-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Create Organisation
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
